import Nullable from '@/types/nullable';
import User from '@/types/user';

export const PERMISSIONS = {
  DISCRETIONARY_MER: 'discretionaryMer',
  STOCKS: 'stocks',
  PROPOSAL_OPTIONS: 'proposalOptions',
  PROPOSAL_DOWNLOAD: 'proposalDownload',
  PROPOSAL_ONLY_SENT_EXTERNALLY: 'proposalOnlySentExternally',
  CALENDAR_YEAR_RETURNS: 'calendarYearReturns',
  BENCHMARKS: 'benchmarks',
  RISK_QUESTIONNAIRE: 'riskQuestionnaire',
  RISK_PROFILE: 'riskProfile',
  ADVANCED_RISK_METRICS: 'advancedRiskMetrics',
  RISK_REWARD_ANALYSIS: 'riskRewardAnalysis',
  DRAWDOWN_ANALYSIS: 'drawdownAnalysis',
  BMO_GIF: 'bmoGif',
  BMO_GIF_LIF_MAX: 'bmoGifLIFMax', // Temporary flag for deployment
  BMO_GIF_DGRE: 'bmoGifDGRE', // Temporary flag for deployment
  CURRENCY_CONVERSIONS: 'currencyConversions',
  MIXED_CURRENCY_WARNING: 'mixedCurrencyWarning',
  INCOME_STREAM: 'incomeStream',
  FIXED_INCOME_METRICS: 'fixedIncomeMetrics',
  EQUITY_METRICS: 'equityMetrics',
  SHORT_HISTORY: 'shortHistory',
  DEFAULT_LONGER_HISTORY: 'defaultLongerHistory',
  MONTH_END_DATA: 'monthEndData',
  PREVIOUS_MONTH_END_DATA: 'previousMonthEndData',
  QUARTER_END_DATA: 'quarterEndData',
  PREVIOUS_QUARTER_END_DATA: 'previousQuarterEndData',
  SHELF_INFORMATION: 'shelfInformation',
  KYP: 'kyp',
  KYP_STOCKS: 'kypStocks',
  KYP_COMPARISONS_SAVED_TO_AUTH_USER: 'kypComparisonsSavedToAuthUser',
  KYP_VIEW_ALTERNATIVES_WITHOUT_SAVING: 'kypViewAlternativesWithoutSaving',
  KYC_UP_RISK_WARNING: 'kycUpRiskWarning',
  CHAT_SUPPORT: 'chatSupport',
  PRODUCT_GUIDE: 'productGuide',
  PORTFOLIO_UNITS_MODE: 'portfolioUnitsMode',
  TAXES: 'taxes',
  DELEGATE_MANAGEMENT: 'delegateManagement',
  DELEGATE_CONTACT_SETTINGS: 'delegateContactSettings',
  BONDS: 'bonds',
  CUSTOM_PROXIES: 'customProxies',
  PREQIN_ALT: 'preqinAlt',
  SUB_ASSET_ALLOCATIONS_DISPLAY: 'subAssetAllocationsDisplay',
  PERSIST_CONTACT_ID_IN_SESSION: 'persistContactIdInSession',

  ADMIN_SECTION_SHOW_USER_UID: 'adminSectionShowUserUId',
  ADMIN_SECTION_SHOW_USER_NAME_ID: 'adminSectionShowNameId',

  INVITE_PEOPLE: 'invitePeople',
  ACTIVITY_LOGS: 'activityLogs',
  PROPOSAL_ITEM_PURPOSE: 'proposalItemPurpose',
  COMPARISON_SET_CURRENT_RECOMMENDED: 'comparisonSetCurrentRecommended',
  SHOW_PORTFOLIO_DETAILS: 'showPortfolioDetails',
  SHOW_EQUITY_ASSET_ALLOCATION: 'showEquityAssetAllocation',
  ESG: 'esg',
  PROPOSAL_TOOLBOX_NEW_MENU: 'proposalToolboxNewMenu',
  PROPOSAL_COBRANDING: 'proposalCoBranding',
  PROPOSAL_PDF_METADATA: 'proposalPdfMetadata',
  COMMON_INCEPTION: 'commonInception',
  USE_MONTHLY_REBALANCE: 'useMonthlyRebalance',
  POST_MESSAGE: 'postMessage',
  FACTSET_MODE_HEADER: 'factsetModeHeader',
  RESTRICTED_MISSING_ITEM_FLOW: 'restrictedMissingItemFlow',
  MANUAL_PROXY_MODAL: 'manualProxyModal',

  // CUSTOM FEATURES
  ACCESSIBLE_UI: 'accessibleUI',
  COMPARISON_METRICS_ACCESSIBLE_TABLE: 'comparisonMetricsAccessibleTable',

  // HISTORICAL SCENARIOS
  HISTORICAL_SCENARIOS: 'historicalScenarios',
  HISTORICAL_SCENARIOS_CUSTOM_CREATION: 'historicalScenariosCustomCreation',

  // RATIONALE QUESTIONNAIRE
  RATIONALE_QUESTIONNAIRE: 'rationaleQuestionnaire',
  RATIONALE_REQUIRED: 'rationaleRequired',
  RATIONALE_WARNING: 'rationaleWarning',
  SECURITIES_WARNING: 'securitiesWarning',

  // SUITABILITY ANALYSIS
  SUITABILITY_ANALYSIS: 'suitabilityAnalysis',
  SUITABILITY_ANALYSIS_REQUIRED: 'suitabilityAnalysisRequired',

  // MAIN SECTIONS
  SECTION_MODELS: 'sectionModels',
  SECTION_SEARCH: 'sectionSearch',
  SECTION_CONTACTS: 'sectionContacts',
  SECTION_COMPARISONS: 'sectionComparisons',
  SECTION_INSIGHTS: 'sectionInsights',
  SECTION_SETTINGS: 'sectionSettings',

  // CONTACTS
  CONTACTS_CREATE: 'contactsCreate',
  CONTACTS_READ: 'contactsRead',
  CONTACTS_CLONE: 'contactsClone',
  CONTACTS_EDIT: 'contactsEdit',
  CONTACTS_DELETE: 'contactsDelete',
  CONTACTS_FILTER: 'contactsFilter',
  CONTACTS_LIST: 'contactsList',
  CONTACTS_DETAILS: 'contactsDetails',
  CONTACTS_RISK_PROFILES_TAB: 'contactsRiskProfilesTab',
  CONTACTS_NOTES: 'contactsNotes',
  CONTACTS_CATEGORY: 'contactsCategory',
  CONTACTS_EMAIL: 'contactsEmail',
  CONTACTS_LAST_NAME: 'contactsLastName',
  CONTACTS_PORTFOLIOS_LIST: 'contactsPortfoliosList',
  CONTACTS_PORTFOLIOS_ACCEPT: 'contactsPortfoliosAccept',
  CONTACTS_LOCALE: 'contactsLocale',
  CONTACTS_TYPE: 'contactsType',
  CONTACTS_STAGE: 'contactsStage',
  CONTACTS_PROVINCE: 'contactsProvince',

  // PORTFOLIOS
  PORTFOLIO_EDITOR_BUILDER: 'portfolioEditorBuilder',
  PORTFOLIO_EDITOR_PINNED: 'portfolioEditorPinned',
  PORTFOLIOS_CREATE: 'portfoliosCreate',
  PORTFOLIOS_READ: 'portfoliosRead',
  PORTFOLIOS_CLONE: 'portfoliosClone',
  PORTFOLIOS_EDIT: 'portfoliosEdit',
  PORTFOLIOS_DELETE: 'portfoliosDelete',
  PORTFOLIOS_FILTER: 'portfoliosFilter',
  PORTFOLIOS_LOAD: 'portfoliosLoad',
  PORTFOLIOS_SAVE_TO_CONTACT: 'portfoliosSaveToContact',
  PORTFOLIOS_SAVE_AS_MODEL: 'portfoliosSaveAsModel',
  PORTFOLIOS_FILE_IMPORT: 'portfoliosFileImport',
  PORTFOLIOS_AI_STATEMENT_READER_BANNER: 'portfoliosAIStatementReaderBanner',
  PORTFOLIOS_AI_STATEMENT_READER: 'portfoliosAIStatementReader',
  PORTFOLIOS_ALLOW_UNALLOCATED: 'portfoliosAllowUnallocated',
  PORTFOLIO_EDITOR_LOCK_TOTAL_VALUE: 'portfolioEditorLockTotalValue',
  PORTFOLIOS_SHOW_PORTFOLIO_ID: 'portfoliosShowPortfolioId',
  PORTFOLIOS_ALLOW_EMPTY_HOLDINGS_IN_LIST: 'portfoliosAllowEmptyHoldingsInList',

  // COMPARISONS
  COMPARISON_AUTO_BENCHMARK: 'comparisonAutoBenchmark',
  COMPARISON_ACCOUNT_SETS: 'comparisonAccountSets',
  COMPARISON_GROUPS: 'comparisonGroups',
  COMPARISON_MARKUP: 'comparisonMarkup',
  COMPARISON_PORTFOLIO_EXPAND_IN_READ_ONLY: 'comparisonPortfolioExpandInReadOnly',
  COMPARISON_MARK_SHARED_WITH_CLIENT: 'comparisonMarkSharedWithClient',
  COMPARISONS_CREATE: 'comparisonsCreate',
  COMPARISONS_READ: 'comparisonsRead',
  COMPARISONS_CLONE: 'comparisonsClone',
  COMPARISONS_EDIT: 'comparisonsEdit',
  COMPARISONS_DELETE: 'comparisonsDelete',
  COMPARISONS_FILTER: 'comparisonsFilter',
  COMPARISONS_SHARE: 'comparisonsShare',
  COMPARISONS_SAVE: 'comparisonsSave',
  COMPARISONS_CLIENT_ACCOUNT: 'comparisonsClientAccount',
  COMPARISONS_MODEL_PORTFOLIO: 'comparisonsModelPortfolio',
  COMPARISONS_NEW_PORTFOLIO: 'comparisonsNewPortfolio',
  COMPARISONS_LOAD_FACTSET_PORTFOLIOS: 'loadFactsetPortfolios',
  COMPARISONS_TOP_HOLDINGS_DYNAMIC_COUNT: 'comparisonsTopHoldingsDynamicCount',

  // MODELS
  USER_MODELS: 'userModels',
  ENTERPRISE_MODELS_LIST: 'enterpriseModelsList',
  ALL_MODELS_LIST: 'allModelsList',
  USER_MODELS_CREATE: 'userModelsCreate',
  USER_MODELS_CLIENT_SIDE_SORTING: 'userModelsClientSideSorting',

  // NOTIFICATION CENTER
  NOTIFICATION_CENTER: 'notificationCenter',

  // EXTERNAL NOTIFICATION CENTER
  EXTERNAL_NOTIFICATION_CENTER: 'externalNotificationCenter',

  // LEGACY
  PROPOSAL_V1_OVERRIDE_ADVISOR_BRANDING: 'proposalV1OverrideAdvisorBranding',
  DATADOG_SESSION_REPLAY: 'datadogSessionReplay',

  // ENTERPRISE ADMIN
  ENTERPRISE_ADMIN: 'enterpriseAdmin',
  ENTERPRISE_ADMIN_GROUPS: 'enterpriseAdminGroups',
  ENTERPRISE_ADMIN_GROUPS_MEMBERS: 'enterpriseAdminGroupsMembers',
  ENTERPRISE_ADMIN_GROUPS_MEMBERS_ADD: 'enterpriseAdminGroupsMembersAdd',
  ENTERPRISE_ADMIN_GROUPS_MEMBERS_REMOVE: 'enterpriseAdminGroupsMembersRemove',
  ENTERPRISE_ADMIN_GROUPS_MODELS: 'enterpriseAdminGroupsModels',
  ENTERPRISE_ADMIN_NOTIFICATION_CENTER: 'enterpriseAdminNotificationCenter',
  ENTERPRISE_ADMIN_DATA_UPLOAD: 'enterpriseAdminDataUpload',

  // PROPOSALS
  FORCED_PAGE_ORDER: 'forcedPageOrder',
  PROPOSAL_ACCESSIBILITY: 'proposalAccessibility', // Temporary feature flag to enable accessibility features in the proposal builder

  // PLANNING SOFTWARE
  PLANNING: 'planning',

  // HOUSEHOLDS
  HOUSEHOLDS: 'households',

  // OMNI
  OMNI_PROPOSAL_FLOW_REWORK: 'omniProposalFlowRework', // Temporary feature flag
  OMNI_REFRESH_QUESTIONNAIRE: 'omniRefreshQuestionnaire',
  OMNI_RISK_PROFILE: 'omniRiskProfile',
  OMNI_RISK_PROFILE_ENFORCE_CONTACT_EMAIL: 'omniRiskProfileEnforceContactEmail',
  OMNI_RISK_PROFILE_SEND_EMAIL_TO_CONTACT: 'omniRiskProfileSendEmailToContact',
  OMNI_RISK_PROFILE_EXISTING_CLIENT_CREATE: 'omniRiskProfileExistingClientCreate',
  PROPOSAL_OMNI: 'proposalOmni',
  OMNI_EXPORT_MODAL_REWORK: 'omniExportModalRework', // Temporary feature flag - https://capintel.atlassian.net/browse/OMNI-2787 To be remove if it becomes the default
  CMS: 'cms', // Permission to access the CMS section in OMNI

  // QUICK SEARCH
  FUZZY_SEARCH: 'fuzzySearch',

  // TAX ANALYSIS
  TAX_ANALYSIS: 'taxAnalysis',

  // ENHANCED SCREENER
  ENHANCED_SCREENER: 'enhancedScreener',
  SCREENER_STOCKS: 'screenerStocks',
  SCREENER_BONDS: 'screenerBonds',
};

export const ENTERPRISE_ADMIN_PERMISSIONS = [
  PERMISSIONS.ENTERPRISE_ADMIN,
  PERMISSIONS.ENTERPRISE_ADMIN_GROUPS,
  PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS,
  PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS_ADD,
  PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS_REMOVE,
  PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MODELS,
  PERMISSIONS.ENTERPRISE_ADMIN_NOTIFICATION_CENTER,
  PERMISSIONS.ENTERPRISE_ADMIN_DATA_UPLOAD,
];

/**
 * Determines if the authenticated user has a permission.
 * Does not validate if the permission was activated by the user. (ex: Advisory Fee)
 *
 * @param {User} user User for which we're validating the access.
 * @param {string} permission Permission to be validated.
 * @returns {boolean} **true** if the user has access, **false** otherwise.
 */
export const userHasPermission = (user: User | null | undefined, permission: string): boolean => {
  if (!user || !user.permissions) {
    return false;
  }

  return user.permissions.includes(permission);
};

/**
 * Determines if the authenticated user has all the permissions.
 * Does not validate if the permission was activated by the user. (ex: Advisory Fee)
 *
 * @param {User} user User for which we're validating the access.
 * @param {string[]} permissions Permissions to be validated.
 * @returns {boolean} **true** if the user has access to all permissions, **false** otherwise.
 */
export const userHasPermissions = (user: User, permissions: string[]): boolean => {
  const userPermissions = user.permissions;
  if (userPermissions === undefined) {
    return false;
  }

  return permissions.every(permission => userPermissions.includes(permission));
};

/**
 * Determines if the authenticated user has one of the permissions and has it enabled.
 *
 * @param {User} user User for which we're validating the access.
 * @param {string} permissions Permissions to be validated.
 * @returns {boolean} **true** if the user has access to any of the permissions, **false** otherwise.
 */
export const userHasPermissionEnabled = (user?: Nullable<User>, ...permissions: string[]): boolean => {
  if (!user?.permissions) {
    return false;
  }

  for (const permission of permissions) {
    if (user.permissions.includes(permission)) {
      return true;
    }
  }

  return false;
};

export const userHasAdminPermission = (user?: Nullable<User>): boolean => {
  if (!user?.permissions) {
    return false;
  }

  for (const permission of user.permissions) {
    if (ENTERPRISE_ADMIN_PERMISSIONS.includes(permission)) {
      return true;
    }
  }

  return false;
};

export default {
  userHasPermissionEnabled,
  userHasPermission,
};
